import { formatCreditLine } from '@/api/wikimediaCommons'

// Credits for Wikimedia images go into the slide notes instead of onto the slide, and follow
// the images: a credit is added when its image lands on the slide and removed when the image
// leaves it (replaced, deleted, crop cancelled before it landed). Every image kind (question,
// option, background, content block, plugin...) keeps the Commons file name in the name of
// its uploaded copy, so matching on it covers them all, including kinds added later. The notes
// are only seen by the presenter, so the section header tells them to show the credit where
// the audience can see it.

export const IMAGE_CREDIT_NOTES_HEADER = 'Image credits (required by the image license; show them where your audience can see, e.g. on this slide or a credits slide)'

const BULLET = '• '
const SOURCE_MARK = 'Wikimedia Commons'
// File names may have brackets (Cup_(cropped).jpg): read up to the bracket that closes the link,
// leaving any punctuation the presenter typed after it.
const COMMONS_FILE_PATTERN = /\/wiki\/File:(\S+?)\)?[.,;]?(?:\s|$)/
// The section is found by its credit lines, not its header, so it is found again whatever
// language the header was written in.
// Every credit line joins its parts with ' / ' or links its Commons file, which keeps a presenter's
// own bullet that merely mentions the site ("• More photos on Wikimedia Commons") out of the section.
const isCreditLine = (text) => text.startsWith(BULLET) && text.includes(SOURCE_MARK)
  && (text.includes(' / ') || COMMONS_FILE_PATTERN.test(text))
const withoutCarriageReturn = (text) => text.replace(/\r$/, '')

// Uploaded copies keep the Commons file name in their stored name, percent-encoded with every
// other character turned into '_': File:Hội_An,_Town.jpg -> 1920px_H_E1_BB_99i_An_2C_Town_cropped_1.webp.
// Both sides are compared as lowercase letters and digits only.
const normalizeForMatch = (text) => text.toLowerCase().replace(/[^a-z0-9]/g, '')
const withoutExtension = (fileName) => fileName.replace(/\.[a-z0-9]+$/i, '')
// A long file name may be cut short in the stored name, so only its start is compared.
const MATCHED_FILE_NAME_LENGTH = 40
// Stored names start with the resized size and the Commons thumbnail width: 1280x720-1920px_...
const withoutSizePrefixes = (fileName) => fileName.replace(/^\d+x\d+-/, '').replace(/^\d+px[-_]/, '')
const STORED_IMAGE_PATTERN = /static\/\d+\/image\/([^"'\\?\s)<>]+)/g
// Commons image URLs also encode ! ' ( ) *, which encodeURIComponent leaves as they are:
// File:Clubs'_Cup_(cropped).jpg is stored as Clubs_27_Cup_28cropped_29_cropped_1.webp.
const encodeLikeCommons = (text) => encodeURIComponent(text)
  .replace(/[!'()*]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`)

const linkedCommonsFile = (line) => COMMONS_FILE_PATTERN.exec(line || '')?.[1] || null
const decodedOrAsWritten = (fileName) => {
  try {
    return decodeURIComponent(fileName)
  } catch (error) {
    // A malformed escape: compare the name as written.
    return fileName
  }
}

export const creditImageStem = (line) => {
  const fileName = linkedCommonsFile(line)
  if (!fileName) return null
  return normalizeForMatch(encodeLikeCommons(decodedOrAsWritten(withoutExtension(fileName))))
    .slice(0, MATCHED_FILE_NAME_LENGTH) || null
}

// The Commons file a credit line links, named as Commons tells files apart: spaces and '_' are
// the same, and so is the case of the first letter, but Flag.svg and Flag.png are two files.
const creditFileName = (line) => {
  const fileName = linkedCommonsFile(line)
  if (!fileName) return null
  const title = decodedOrAsWritten(fileName).replace(/ /g, '_')
  return title.charAt(0).toUpperCase() + title.slice(1)
}

// A credit line already lists the image when it is the same line or links the same file, so a
// line written in an older format, or edited by the presenter, is not added a second time.
const isListed = (creditLines, line) => {
  const fileName = creditFileName(line)
  return creditLines.some((listed) => listed === line || (fileName && creditFileName(listed) === fileName))
}

// Fields that are not the image shown on the slide: the source kept for re-cropping (origin*,
// which stays after the image is deleted), local copies (*Cache) and crop settings.
const NOT_SHOWN_IMAGE_FIELD = /^origin|Cache$|CropperData$/
const withoutHiddenImageFields = (key, value) => (NOT_SHOWN_IMAGE_FIELD.test(key) ? undefined : value)

const shownImageText = (source) => {
  try {
    return typeof source === 'string' ? source : JSON.stringify(source, withoutHiddenImageFields)
  } catch (error) {
    return ''
  }
}

// Normalized names of the uploaded images shown anywhere in the given slide data.
export const storedImageNames = (...sources) => {
  const names = new Set()
  sources.forEach((source) => {
    if (!source) return
    const text = shownImageText(source)
    Array.from(text.matchAll(STORED_IMAGE_PATTERN))
      .forEach(([, fileName]) => names.add(normalizeForMatch(withoutSizePrefixes(withoutExtension(fileName)))))
  })
  names.delete('')
  return [...names].sort()
}

// True while an image on the slide is still a local preview waiting for its upload: its stored
// copy is not there yet, so the slide's images are not settled.
export const hasLocalImagePreview = (...sources) => sources
  .some((source) => Boolean(source) && shownImageText(source).includes('blob:'))

// Stored names end with the crop suffix and an upload timestamp (..._cropped_1791176208426).
const STORED_NAME_SUFFIX = /^(cropped\d*|\d{10,})?$/

// The stored name must be the Commons file name itself, not merely contain it, so an upload of
// vacation_cat.jpg does not match File:Cat.jpg and Foo_10 does not match File:Foo_1.
// A long file name is cut short in the credit stem, so then anything may follow it.
const isCopyOf = (name, stem) => name.startsWith(stem)
  && (stem.length >= MATCHED_FILE_NAME_LENGTH || STORED_NAME_SUFFIX.test(name.slice(stem.length)))

const creditMatchesImages = (line, names) => {
  const stem = creditImageStem(line)
  return Boolean(stem) && names.some((name) => isCopyOf(name, stem))
}

// Credit lines seen in this session, one per image, so a credit can come back when its image
// does (undo, re-pick) after the line was removed.
const MAX_REMEMBERED_CREDITS = 200
const sessionCredits = new Map()

export const rememberImageCredit = (line) => {
  const stem = creditImageStem(line)
  if (!stem) return
  sessionCredits.delete(stem)
  sessionCredits.set(stem, line)
  if (sessionCredits.size > MAX_REMEMBERED_CREDITS) {
    sessionCredits.delete(sessionCredits.keys().next().value)
  }
}

export const knownImageCredits = () => [...sessionCredits.values()]

// A pick waiting for its image to land, per slide and picker. When the presenter leaves the
// slide before the upload finished, its stored copy never shows in this browser (the left
// slide keeps the local preview), so the pick is credited as it was made. Opening the same
// picker again forgets it: the crop was cancelled or another image is being chosen.
const PENDING_PICK_MAX_AGE_MS = 2 * 60 * 1000
const pendingPicks = new Map()
const pendingPickKey = (slideId, pickerType) => `${slideId}|${pickerType}`

export const rememberPendingPick = (slideId, pickerType, line) => {
  pendingPicks.set(pendingPickKey(slideId, pickerType), { slideId, line, at: Date.now() })
}

export const clearPendingPick = (slideId, pickerType) => pendingPicks.delete(pendingPickKey(slideId, pickerType))

// The slide's pending picks still young enough to be uploading; each is handed over once.
export const takePendingPicks = (slideId, now = Date.now()) => {
  const lines = []
  pendingPicks.forEach((pick, key) => {
    if (pick.slideId !== slideId) return
    pendingPicks.delete(key)
    if (now - pick.at <= PENDING_PICK_MAX_AGE_MS) lines.push(pick.line)
  })
  return lines
}

export const creditLinesInNotes = (notes) => (notes || '').split('\n').map(withoutCarriageReturn).filter(isCreditLine)

// Which credit lines to add and remove after the slide's images changed. A credit is added only
// for an image new to the slide (a re-crop of one already there adds nothing, so a credit the
// presenter deleted stays deleted). Only credit lines are ever removed, and only when no image
// left on the slide still matches them.
export const planImageCreditChanges = ({
  notes, addedNames, removedNames, currentNames, previousNames = [], knownLines,
}) => {
  const present = creditLinesInNotes(notes)
  const addLines = [...new Set(knownLines)]
    .filter((line) => !isListed(present, line)
      && creditMatchesImages(line, addedNames)
      && !creditMatchesImages(line, previousNames))
  const removeLines = present
    .filter((line) => creditMatchesImages(line, removedNames) && !creditMatchesImages(line, currentNames))
  return { addLines, removeLines }
}

const isWebLink = (url) => typeof url === 'string' && /^https?:\/\//i.test(url)

export const buildImageCreditNoteLine = (item) => `${BULLET}${formatCreditLine(item, {
  source: isWebLink(item.sourceUrl) ? item.sourceUrl : '',
  license: isWebLink(item.licenseUrl) ? item.licenseUrl : '',
})}`

// Adds the line to the credits section, creating the section at the end when there is none.
// The presenter's own text is never changed, and an image already listed is not added again.
export const appendImageCreditToNotes = (notes, line, header) => {
  const current = notes || ''
  // Notes pasted from Windows keep '\r\n'; compare without the '\r' so a repeat pick still matches.
  const lines = current.split('\n')
  const plainLines = lines.map(withoutCarriageReturn)
  if (plainLines.includes(line) || isListed(plainLines.filter(isCreditLine), line)) return current

  // After the last credit line; else right under a header the presenter kept after deleting
  // every credit, so the section is not started twice.
  const lastCreditIndex = plainLines.map(isCreditLine).lastIndexOf(true)
  const anchorIndex = lastCreditIndex === -1 ? plainLines.lastIndexOf(header) : lastCreditIndex
  if (anchorIndex === -1) {
    const ownText = current.replace(/\s+$/, '')
    return ownText ? `${ownText}\n\n${header}\n${line}` : `${header}\n${line}`
  }

  lines.splice(anchorIndex + 1, 0, line)
  return lines.join('\n')
}

// Removes the given credit lines. With the last credit gone, the section header and the blank
// line put before it go too, so the notes read as the presenter wrote them. Only the exact
// header is removed: a line the presenter wrote there stays.
export const removeImageCreditsFromNotes = (notes, linesToRemove, header) => {
  const current = notes || ''
  const lines = current.split('\n')
  const plainLines = lines.map(withoutCarriageReturn)
  const dropped = new Set(plainLines
    .map((text, index) => (isCreditLine(text) && linesToRemove.includes(text) ? index : -1))
    .filter((index) => index !== -1))
  if (!dropped.size) return current

  const remainingCredits = plainLines.filter((text, index) => isCreditLine(text) && !dropped.has(index))
  if (!remainingCredits.length) {
    const headerIndex = plainLines.findIndex(isCreditLine) - 1
    if (headerIndex >= 0 && plainLines[headerIndex] === header) {
      dropped.add(headerIndex)
      if (headerIndex >= 1 && !plainLines[headerIndex - 1].trim()) dropped.add(headerIndex - 1)
    }
  }
  return lines.filter((text, index) => !dropped.has(index)).join('\n')
}

export const applyImageCreditChanges = (notes, { addLines, removeLines }, header) => addLines.reduce(
  (result, line) => appendImageCreditToNotes(result, line, header),
  removeImageCreditsFromNotes(notes, removeLines, header),
)
